import {
    secureGet,
    securePost,
    securePut,
    secureDelete,
} from '../../Config/ApiConfig';



/**
 * Get all channels for current user
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.size - Page size
 * @param {string} params.search - Search string
 * @param {Object} params.filters - Filter object
 * @param {string} params.sort_by - Sort field
 * @param {string} params.sort_direction - Sort direction (asc/desc)
 * @returns {Promise} API response
 */
export const getChannels = async ({ page, size, search, filters, sort_by, sort_direction }) => {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page);
    if (size) queryParams.append('page_size', size);
    if (search) queryParams.append('search_string', search);
    if (filters && Object.keys(filters).length > 0) {
        queryParams.append('filters', JSON.stringify(filters));
    }
    if (sort_by) queryParams.append('sort_by', sort_by);
    if (sort_direction) queryParams.append('sort_direction', sort_direction);

    const { data } = await secureGet(`/channels?${queryParams.toString()}`);
    return data;
};

/**
 * Get channel by ID
 * @param {string} id - Channel ID
 * @returns {Promise} API response
 */
export const getChannelById = async (id) => {
    const { data } = await secureGet(`/channels/${id}`);
    return data;
};

/**
 * Create a new channel
 * @param {Object} body - Channel data
 * @returns {Promise} API response
 */
export const createChannel = async (body) => {
    const { data } = await securePost('/channels', body);
    return data;
};

/**
 * Update channel
 * @param {string} id - Channel ID
 * @param {Object} body - Updated channel data
 * @returns {Promise} API response
 */
export const updateChannel = async (id, body) => {
    const { data } = await securePut(`/channels/${id}`, body);
    return data;
};

/**
 * Refresh a channel access token from its linked credential
 * @param {string} id - Channel ID
 * @returns {Promise} API response
 */
export const refreshChannelToken = async (id) => {
    const { data } = await securePost(`/channels/${id}/refresh-token`);
    return data;
};

/**
 * Delete channel
 * @param {string} id - Channel ID
 * @returns {Promise} API response
 */
export const deleteChannel = async (id) => {
    const { data } = await secureDelete(`/channels/${id}`);
    return data;
};

/**
 * Create virtual contact for a channel
 * @param {string} channelId - Channel ID
 * @param {Object} body - Contact data
 * @returns {Promise} API response
 */
export const createVirtualContact = async (channelId, body) => {
    const { data } = await securePost(`/channels/${channelId}/virtual-contacts`, body);
    return data;
};

/**
 * Create historical import for a channel
 * @param {string} channelId - Channel ID
 * @param {Object} body - Import data with file_upload_id
 * @returns {Promise} API response
 */
export const createHistoricalImport = async (channelId, body) => {
    const { data } = await securePost(`/channels/${channelId}/historical-imports`, body);
    return data;
};

/**
 * Get all historical imports for a channel
 * @param {string} channelId - Channel ID
 * @param {Object} params - Query parameters
 * @returns {Promise} API response
 */
export const getHistoricalImports = async (channelId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.size) queryParams.append('page_size', params.size);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction);
    if (params.filters && Object.keys(params.filters).length > 0) {
        queryParams.append('filters', JSON.stringify(params.filters));
    }

    const { data } = await secureGet(`/channels/${channelId}/historical-imports?${queryParams.toString()}`);
    return data;
};

/**
 * Get specific historical import
 * @param {string} importId - Import ID
 * @returns {Promise} API response
 */
export const getHistoricalImportById = async (importId) => {
    const { data } = await secureGet(`/channels/historical-imports/${importId}`);
    return data;
};

/**
 * Delete historical import
 * @param {string} importId - Import ID
 * @returns {Promise} API response
 */
export const deleteHistoricalImport = async (importId) => {
    const { data } = await secureDelete(`/channels/historical-imports/${importId}`);
    return data;
};
